import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { DateTimePicker } from '@/components/shared/DateTimePicker';

interface ScheduleRestartModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: string;
  environment: string;
  onSchedule: (restartTimestamp: string) => Promise<void>;
}

export const ScheduleRestartModal = ({
  open,
  onOpenChange,
  client,
  environment,
  onSchedule,
}: ScheduleRestartModalProps) => {
  const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSchedule = async () => {
    if (!selectedDateTime) {
      setError('Please select a date and time');
      return;
    }

    // Validate it's in the future
    if (selectedDateTime <= new Date()) {
      setError('Restart time must be in the future');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSchedule(selectedDateTime.toISOString());
      // Reset and close modal
      setSelectedDateTime(undefined);
      onOpenChange(false);
    } catch (err) {
      // Error handled by parent component via toast
      setError('Failed to schedule restart');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedDateTime(undefined);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Schedule Server Restart</DialogTitle>
          <DialogDescription>
            Schedule a restart for <span className='font-semibold'>{client}</span> •{' '}
            <span className='font-semibold'>{environment}</span>
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* DateTimePicker */}
          <DateTimePicker
            value={selectedDateTime}
            onChange={setSelectedDateTime}
            minDate={new Date()}
          />

          {/* Error Message */}
          {error && (
            <div className='rounded-md bg-destructive/10 p-3'>
              <p className='text-sm text-destructive'>{error}</p>
            </div>
          )}

          {/* Info Message */}
          <div className='rounded-md bg-blue-50 p-3 border border-blue-200'>
            <p className='text-xs text-blue-700'>
              The server will automatically restart at the scheduled time. You will receive a
              notification when the restart completes.
            </p>
          </div>

          {/* Actions */}
          <div className='flex justify-end gap-2'>
            <Button type='button' variant='outline' onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleSchedule}
              disabled={isSubmitting || !selectedDateTime}
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Restart'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
